import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaPen,
  FaTrash,
  FaChevronDown,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { IoIosApps } from "react-icons/io";
import { DeleteModal } from "../Fields/FieldDetails";
import { UserContext } from "../../UserContext";
import axios from "axios";
import "./Problems.css";
import toast from "react-hot-toast";

const CustomStatusDropdown = ({ currentStatus, onStatusChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const statuses = [
    { key: "notStarted", label: "Not Started" },
    { key: "inProgress", label: "In Progress" },
    { key: "finished", label: "Finished" },
  ];

  const selectedStatus =
    statuses.find((s) => s.key === currentStatus) || statuses[0];

  const handleSelect = (statusKey) => {
    onStatusChange(statusKey);
    setIsOpen(false);
  };

  const statusClassMap = {
    finished: "status-finished",
    notStarted: "status-not-started",
    inProgress: "status-in-progress",
  };

  return (
    <div className="task-status-wrapper">
      <button
        className={`task-status-badge ${statusClassMap[currentStatus]}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {t(selectedStatus.label)}
        <FaChevronDown className="task-status-icon" />
      </button>
      {isOpen && (
        <div className="task-status-menu">
          {statuses.map((status) => (
            <div
              key={status.key}
              className="task-status-item"
              onClick={() => handleSelect(status.key)}
            >
              <span
                className={`status-label-badge status-item-${status.key} ${
                  statusClassMap[status.key]
                }`}
              >
                {t(status.label)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProblemDetails = () => {
  const { t } = useTranslation();
  const { problemId } = useParams();
  const navigate = useNavigate();
  // START: MODIFIED - Destructure userData from UserContext
  const { token, userData } = useContext(UserContext);
  // END: MODIFIED
  const [problem, setProblem] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    const fetchProblem = async () => {
      if (!problemId || !token) return;
      try {
        const response = await axios.get(`api/problem/${problemId}`, {
          headers: { Authorization: token },
        });
        setProblem(response.data.items[0]);
      } catch (error) {
        console.error("Failed to fetch problem details:", error);
        toast.error(t("Could not load problem details."));
        navigate("/problems");
      }
    };
    fetchProblem();
  }, [problemId, token, navigate, t]);

  // START: UPDATED handleStatusChange to send all fields
  const handleStatusChange = async (newStatus) => {
    const originalProblemState = { ...problem };

    setProblem((prevProblem) => ({
      ...prevProblem,
      problem: { ...prevProblem.problem, state: newStatus },
    }));

    try {
      // Create a complete payload by spreading the existing problem data.
      // This ensures no fields are accidentally deleted by the backend.
      const problemPayload = {
        ...problem.problem, // Spread all existing data
        state: newStatus, // Overwrite only the state
      };

      // To prevent a potential 500 error, remove backend-managed timestamps
      delete problemPayload.createTs;
      delete problemPayload.updateTs;

      const formData = new FormData();
      formData.append("problem", JSON.stringify(problemPayload));

      await axios.put("api/problem/updateProblemWithAudioAndImages", formData, {
        headers: { Authorization: token },
      });

      toast.success(t("Problem status updated successfully!"));
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error(t("Failed to update status. Reverting change."));
      setProblem(originalProblemState);
    }
  };
  // END: UPDATED handleStatusChange

  const handleDelete = async () => {
    try {
      await axios.delete(`api/problem/${problemId}`, {
        headers: { Authorization: token },
      });
      toast.success(
        `${t("Problem")} "${problem.problem?.name}" ${t("deleted")}.`
      );
      setDeleteModalOpen(false);
      navigate(`/problems`);
    } catch (error) {
      console.error("Failed to delete problem:", error);
      toast.error(t("Failed to delete problem."));
    }
  };

  if (!problem || !problem.problem) return <div>{t("Loading...")}</div>;

  const problemName = problem.problem?.name || t("Unnamed Problem");
  const imageUrls = problem.problem?.imagesUrls
    ? problem.problem.imagesUrls.split(",").filter((url) => url)
    : [];

  // START: NEW LOGIC for conditional field/batch display
  let scopeDisplayValue;
  if (problem.problem.fieldId && problem.field) {
    // If a field is assigned, display its name
    scopeDisplayValue = problem.field.name;
  } else {
    // If no field is assigned, check the farm type
    if (userData?.Farm?.type === "hydroponic") {
      scopeDisplayValue = t("All Batches");
    } else {
      // Default to "All Fields" for "soil" farms or if type is unknown
      scopeDisplayValue = t("All Fields");
    }
  }
  // END: NEW LOGIC

  return (
    <>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        itemName={problemName}
      />
      <div className="task-details-page">
        <header className="task-details-main-header">
          <h1 className="header-title">{t("Problem Details")}</h1>
          <div className="header-actions">
            <button
              className="button-edit"
              onClick={() => navigate(`/problems/${problemId}/edit`)}
            >
              <FaPen /> {t("Edit Problem")}
            </button>
            <button
              className="button-danger-outline"
              onClick={() => setDeleteModalOpen(true)}
            >
              <FaTrash /> {t("Delete Problem")}
            </button>
          </div>
        </header>

        <div className="task-details-card">
          <div className="task-details-sub-header">
            <h2>{problemName}</h2>
            <CustomStatusDropdown
              currentStatus={problem.problem.state}
              onStatusChange={handleStatusChange}
            />
          </div>

          <div className="task-details-main-grid">
            <div className="task-details-item">
              <span className="task-details-label">
                <FaRegCalendarAlt /> {t("Date")}
              </span>
              <span className="task-details-value">
                {new Date(problem.problem.problemDate).toLocaleDateString()}
              </span>
            </div>

            {/* START: UPDATED JSX to display the conditional scope value */}
            <div className="task-details-item">
              <span className="task-details-label">
                <IoIosApps /> {t("Field")}
              </span>
              <span className="task-details-value">{scopeDisplayValue}</span>
            </div>
            {/* END: UPDATED JSX */}
          </div>

          <div className="task-details-secondary-grid">
            {problem.problem.textDesc && (
              <div className="task-details-item full-width">
                <span className="task-details-label">{t("Description")}</span>
                <p className="task-details-value desc-text">
                  {problem.problem.textDesc}
                </p>
              </div>
            )}

            {imageUrls.length > 0 && (
              <div className="task-details-item full-width">
                <span className="task-details-label">{t("Photos")}</span>
                <div className="task-photo-gallery">
                  {imageUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`${t("problem attachment")} ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {problem.problem.audioDesc && (
              <div className="task-details-item full-width">
                <span className="task-details-label">{t("Voice Note")}</span>
                <audio
                  src={problem.problem.audioDesc}
                  controls
                  className="task-audio-player"
                ></audio>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProblemDetails;
